import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { apiDelete, apiPatch, operationsClient } from '@/lib/api';
import { timesheetEndpoints } from '@/lib/api/endpoints';
import { timeEntryKeys } from '@/lib/queryKeys';
import type { TimeEntry, UpdateTimeEntryDto } from '@/types/timeEntry';

/**
 * The override valve, scoped to a job's appointment rather than the timesheet
 * grid.
 *
 * Same routes as the timesheet-side override hooks; the difference is which
 * caches they invalidate. The job page reads time entries through
 * `timeEntryKeys.appointment` / `jobRollup`, so an override made there has to
 * refresh those or the row keeps showing its pre-correction values.
 */
export function useOverrideUpdateTimeEntry(appointmentId: string, jobId?: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    TimeEntry,
    { id: string; dto: UpdateTimeEntryDto & { reason: string } }
  >({
    mutationFn: ({ id, dto }) =>
      apiPatch<TimeEntry, UpdateTimeEntryDto & { reason: string }>(
        operationsClient,
        timesheetEndpoints.overrideEntry(id),
        dto,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.appointment(appointmentId) });
      if (jobId) {
        void queryClient.invalidateQueries({ queryKey: timeEntryKeys.jobRollup(jobId) });
      }
      toast.success('Locked entry corrected — recorded in the audit trail');
    },
  });
}

export function useOverrideDeleteTimeEntry(appointmentId: string, jobId?: string) {
  const queryClient = useQueryClient();
  return useApiMutation<unknown, { id: string; reason: string }>({
    // DELETE with a body — axios only sends one through `config.data`.
    mutationFn: ({ id, reason }) =>
      apiDelete(operationsClient, timesheetEndpoints.overrideEntry(id), { data: { reason } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: timeEntryKeys.appointment(appointmentId) });
      if (jobId) {
        void queryClient.invalidateQueries({ queryKey: timeEntryKeys.jobRollup(jobId) });
      }
      toast.success('Locked entry deleted — recorded in the audit trail');
    },
  });
}
